import { FieldMapConfig } from "./BigQuery/FieldMaps";
import { TableMapType } from "./BigQuery/TableMaps";
import { Customer } from "./customer";
import { FieldFilters, QuantityFilterCondition } from "./Form";
import { MarketingSegmentCategory } from "./MarketingSegments";
import { ProductSizeRef } from "./product";

export enum CustomerSegmentScope {
  Group = "Group",
  Restaurant = "Restaurant",
}

/**
 * Booking customer scope for segment filtering (Venn diagram concept)
 * A = booking customers (primary bookers), B = guests
 */
export enum BookingCustomerType {
  AnyBookingCustomers = "any_booking_customers", // A: anyone who made a booking
  AnyGuests = "any_guests", // B: anyone who appeared as guest
  Any = "any", // A ∪ B: union of both (default)
  OnlyBookingCustomers = "only_booking_customers", // A - B: booking customers who never been guest
  OnlyGuests = "only_guests", // B - A: guests who never made a booking
  BookingCustomersAndGuests = "booking_customers_and_guests", // A ∩ B: intersection - both booked AND appeared as guest
}

export const BookingCustomerTypeInfo = {
  any_booking_customers: "made a booking",
  any_guests: "appeared as guest",
  any: "made a booking and/or who appeared as guest",
  only_booking_customers: "have booked but never been guest",
  only_guests: "appeared as guests but have never made a booking",
  booking_customers_and_guests: "have booked AND appeared as guest",
} as const;

export interface SegmentCategory {
  id: string;
  _key?: string;
  name: string;
  enabled: boolean;
}

export interface SegmentCategories {
  [id: string]: SegmentCategory;
}

export interface CustomerSegment {
  _key?: string;
  id: string;
  name: string;
  restaurantId?: string; // null === All venues
  category?: MarketingSegmentCategory;
  /** @deprecated superseded by `category` */
  segmentCategoryId?: string; // Reference to SegmentCategory (legacy)
  description: string;
  scope: CustomerSegmentScope;
  enabled: boolean;
  filters: CustomFilters; // See *FieldMapSet for supported Filters
  selectedTables: TableMapType[];
  bookingCustomerType?: BookingCustomerType;
  /** "Match within" scope for the Purchase filter (product-scoped rows) */
  purchaseMatchWithin?: PurchaseScope;
  order?: number;
  /**
   * System-reserved segment seeded to back a pre-built automation
   * (name is one of ReservedSegmentName, immutable for the app's lifetime).
   */
  isReserved?: boolean;
  /** Stable catalogue key to ensure segments remain immutable for the app's lifetime */
  sourceKey?: string;
  /** SA-controlled provisioning gate for pre-built (seeded) segments */
  provisioned?: boolean;
}

export interface CustomerSegments {
  [id: string]: CustomerSegment;
}

/**
 *   BuiltIn  — reserved segments that backs pre-built automations (locked)
 *   Curated  — pre-built marketing segments seeded by SA and are editable
 *   Custom   — one taht tenant creates
 */
export enum SegmentModel {
  BuiltIn = "built_in",
  Curated = "curated",
  Custom = "custom",
}

export const SegmentModelLabel: Record<SegmentModel, string> = {
  [SegmentModel.BuiltIn]: "Builtin",
  [SegmentModel.Curated]: "Curated",
  [SegmentModel.Custom]: "Custom",
};

export const SegmentModelOrder: SegmentModel[] = [SegmentModel.BuiltIn, SegmentModel.Curated, SegmentModel.Custom];

export const getSegmentModel = (segment: Pick<CustomerSegment, "isReserved" | "sourceKey">): SegmentModel => (segment.isReserved ? SegmentModel.BuiltIn : segment.sourceKey ? SegmentModel.Curated : SegmentModel.Custom);

/**
 * Additional CustomFilter on given CustomeFilter
 * use case: product requirement attached to a `transaction__*` filters.
 * this brings "outer" arbitary product and product group filters to be more deterministric
 * by "attaching" togiven filter.
 */
export interface WithCustomFilter {
  field: "products" | "product_groups" | string;
  condition: string;
  // id of product group or list of selected product with its sizes
  value: (string | ProductSizeRef)[];
}
export interface CustomFilter {
  field: string;
  condition: string;
  value: any;
  valueFrom?: any;
  valueTo?: any;
  mapSetType: TableMapType;
  conditionLabel?: string;
  /**
   * Event-automation gate fro onDocumentCreated / onDocumentWritten events. When true, this predicate only
   * matches if the field's value actually CHANGED
   */
  requireChange?: boolean;
  /**
   * Runtime field configuration for dynamically injected fields
   * (e.g., __allergies, __dietaryRequirements, __tags)
   * that don't exist in predefined FieldMapSet
   */
  runtimeFieldConfig?: FieldMapConfig;
  /**
   * Optional product requirement(s) scoping this condition to matching line items. A condition with
   * withFilters becomes a product-scoped aggregate; several such rows (uniquely keyed, e.g. transaction_quantity_a)
   * AND together to require multiple independent product groups at once.
   */
  withFilters?: WithCustomFilter[];
}

export interface CustomFilters {
  [key: string]: CustomFilter;
}

/**
 * "Match within" — one global scope for the Purchase filter: the boundary the per-row product
 * aggregates group by. All three correlation columns live on transaction_line_item.
 */
export enum PurchaseScope {
  Transaction = "transaction", // "One purchase"   — within a single transaction (fk_transaction_id)
  Booking = "booking", // "Same booking purchases" — across a booking's transactions (booking_id)
  Customer = "customer", // "Whole history"        — anywhere in the customer's transactions (customer_id)
}

export interface QueryBuilderOptions {
  clientId: string;
  restaurantId?: string; // Single restaurant ID (simplified from multi-select for timezone support)
  limit?: number;
  offset?: number;
  primaryTable?: TableMapType; // Which table to query (customer, booking, transaction)
  usedTables?: TableMapType[]; // Selected filter categories for query structure (JOINs, WITH, SELECT)
  bookingCustomerType?: BookingCustomerType; // Filter booking counts by customer type
  timezone?: string; // IANA timezone string (e.g., "Australia/Sydney") for date calculations
  paginateResult?: boolean; // return paginated result must use limit and offset
  purchaseMatchWithin?: PurchaseScope; // DT-8027: scope for product-scoped purchase rows (default Customer)
}

// ********* DEPRECATED DEFINITIONS *******************/
/** @deprecated by CustomerSegment */
interface CustomerFilterBase {
  _key?: string;
  description: string;
  restaurantId: string;
  name: string;
  enabled: boolean;
  order?: number;
  membershipFilter?: CustomerFilterMembershipFilter;
}

/** @deprecated use CustomerSegment based type defs */
export enum FilterType {
  Purchases = "purchases",
  MenuBooked = "menu-booked",
  Occassions = "occassions",
  Visits = "visits",
  LastVisits = "last-visits",
  FirstTimeCustomer = "first-time-customers",
  GuestNeverBooked = "guest-never-booked",
  NoShows = "no-shows",
  Cancellations = "cancellations",
  Waitlists = "waitlists",
  CustomerTags = "customerTags",
  Allergies = "allergies",
  DietaryPreference = "dietary",
  Membership = "membership",
  Age = "age",
  Birthday = "birthday",
  Custom = "custom",
}

/** @deprecated because Membership is FilterType */
export enum CustomerFilterMembershipFilter {
  All = 1,
  Member = 2,
  NonMember = 3,
}
/** @deprecated */
interface CustomerFilterBase {
  _key?: string;
  description: string;
  restaurantId: string;
  name: string;
  enabled: boolean;
  order?: number;
  membershipFilter?: CustomerFilterMembershipFilter;
}

/** @deprecated */
export interface CustomerFilters {
  [id: string]: CustomerFilter;
}

/** @deprecated replaced by CustomerSegment */
export type CustomerFilter =
  | (CustomerFilterBase & {
      filterType: FilterType.Purchases;
      filters: {
        productIds: { [id: string]: { productSizeIds: { [sizeId: string]: boolean } } };
        purchases: {
          condition: QuantityFilterCondition;
          isBetweenConditionFirstValue?: number;
          isBetweenConditionSecondValue?: number;
          value?: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.MenuBooked;
      filters: {
        menuIds: { [id: string]: boolean };
        menuBooked: {
          condition: QuantityFilterCondition;
          isBetweenConditionFirstValue?: number;
          isBetweenConditionSecondValue?: number;
          value?: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Occassions;
      filters: {
        occasionIds: string[];
        occasions: {
          condition: QuantityFilterCondition;
          isBetweenConditionFirstValue?: number;
          isBetweenConditionSecondValue?: number;
          value?: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Visits;
      filters: {
        visits: {
          condition: QuantityFilterCondition;
          value: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.LastVisits;
      filters: {
        lastVisits: {
          condition: string;
          value: string;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.NoShows;
      filters: {
        noShows: {
          condition: QuantityFilterCondition;
          hasAttended?: boolean;
          value: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Cancellations;
      filters: {
        cancellation: {
          condition: QuantityFilterCondition;
          hasAttended?: boolean;
          value: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Waitlists;
      filters: {
        waitlists: {
          condition: QuantityFilterCondition;
          hasAttended?: boolean;
          value: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.CustomerTags;
      filters: {
        customerTagIds: { [id: string]: boolean };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Allergies;
      filters: {
        allergies: string[];
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.DietaryPreference;
      filters: {
        dietaryRequirements: string[];
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Membership;
      filters: {
        membershipIds: { [id: string]: boolean };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Age;
      filters: {
        age: {
          ageFrom: string;
          ageTo: string;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.FirstTimeCustomer;
    })
  | (CustomerFilterBase & {
      filterType: FilterType.GuestNeverBooked;
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Age;
      filters: {
        age: {
          ageFrom: string;
          ageTo: string;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Birthday;
      filters: {
        birthday: {
          daysBeforeBirthday: number;
        };
      };
    })
  | (CustomerFilterBase & {
      filterType: FilterType.Custom;
      filters: FieldFilters<Customer>;
    });

/** @deprecated */
interface AutomatedCustomerTagDataBase {
  customerFilterId: string;
  customerId: string;
  customerTagId: string;
  customerTagCategoryId: string;
}

/** @deprecated */
export type AutomatedCustomerTagData =
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.Purchases;
      purchases: {
        purchaseCount: number;
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.MenuBooked;
      menuBooked: {
        menuBookingCount: number;
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.Occassions;
      occasions: {
        occasionBookingCount: number;
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.Visits;
      visits: {
        visitCount: number;
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.LastVisits;
      lastVisit: {
        lastVisitDate: number; // YYYYMMDD
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.NoShows;
      noShow: {
        noShowBookingCount: number;
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.Cancellations;
      cancellation: {
        cancellationBookingCount: number;
      };
    })
  | (AutomatedCustomerTagDataBase & {
      filterType: FilterType.Waitlists;

      waitlist: {
        waitlistBookingCount: number;
      };
    });

/**
 * These are internally wired with pre-built provisioned automations
 */
export enum ReservedSegmentName {
  AnniversaryYearCustomer = "ANNIVERSARY_YEAR_CUSTOMER",
  AnniversaryYearMembership = "ANNIVERSARY_YEAR_MEMBERSHIP",
  BirthdayInDaysCustomer = "BIRTHDAY_IN_DAYS_CUSTOMER",
  BirthdayInDaysMember = "BIRTHDAY_IN_DAYS_MEMBER",
  IncompleteBookingsSinceDays = "INCOMPLETE_BOOKINGS_SINCE_DAYS",
  IncompleteFunctionBookingsSinceDays = "INCOMPLETE_FUNCTION_BOOKINGS_SINCE_DAYS",
  LastVisitSinceDays_1 = "LAST_VISIT_SINCE_DAYS_1",
  LastVisitSinceDays_2 = "LAST_VISIT_SINCE_DAYS_2",
  LastVisitSinceDays_3 = "LAST_VISIT_SINCE_DAYS_3",
  LastVisitSinceDays_4 = "LAST_VISIT_SINCE_DAYS_4",
  LastVisitSinceDaysX = "LAST_VISIT_SINCE_DAYS_X",
  LastVisitSinceDaysY = "LAST_VISIT_SINCE_DAYS_Y",
  LastVisitSinceDaysZ = "LAST_VISIT_SINCE_DAYS_Z",
  PostPurchaseSinceDays = "POST_PURCHASE_SINCE_DAYS",
  PreOrderOpenInDays = "PRE_ORDER_OPEN_IN_DAYS",
  PostVisit_1 = "POST_VISIT_1",
  PostVisit_2 = "POST_VISIT_2",
  PostVisit_3 = "POST_VISIT_3",
  PostVisit_4 = "POST_VISIT_4",
  UpcomingBookingInDays = "UPCOMING_BOOKING_IN_DAYS",
  MembershipSinceDays = "MEMBERSHIP_SINCE_DAYS",
}
